import { fetchAppointments } from '@ctw/shared/api/fhir/appointments';
import { getDocumentReferences } from '@ctw/shared/api/fhir/document';
import { getLensEncountersFQS } from '@ctw/shared/api/fhir/encounters';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { ReferralModel } from '@ctw/shared/api/fhir/models/referral';
import { fqsRequestAll, MAX_OBJECTS_PER_REQUEST } from '@ctw/shared/api/fqs/client';
import {
  type ServiceRequestGraphqlResponse,
  ServiceRequestQuery,
} from '@ctw/shared/api/fqs/queries/service-requests';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  findRelatedAppointment,
  findRelatedEncounter,
} from '@ctw/shared/hooks/use-patient-referrals';
import type { Env } from '@ctw/shared/utils/environments';
import type { GraphQLClient } from 'graphql-request';

export async function fetchReferralsByIdFQS(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string>,
  env: Env,
) {
  const { data } = await fqsRequestAll<ServiceRequestGraphqlResponse>(
    telemetry,
    graphqlClient,
    ServiceRequestQuery,
    'ServiceRequestConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      sort: {},
      filter: {
        ids: {
          anymatch: ids,
        },
      },
    },
  );

  const appointments = await fetchAppointments(telemetry, graphqlClient, patient);
  const encounters = await getLensEncountersFQS(telemetry, graphqlClient, patient, env);
  const documents = await getDocumentReferences(telemetry, graphqlClient, patient, [
    MAX_OBJECTS_PER_REQUEST,
  ]);

  return data.ServiceRequestConnection.edges
    .map((x) => x.node)
    .map((serviceRequest) => {
      const relatedAppointment = findRelatedAppointment(serviceRequest, appointments);
      const relatedEncounter = relatedAppointment
        ? findRelatedEncounter(
            relatedAppointment,
            encounters.map((encounterModel) => encounterModel.resource),
          )
        : undefined;

      return new ReferralModel(
        serviceRequest,
        patient,
        relatedAppointment,
        relatedEncounter,
        documents,
      );
    });
}
